import { ReactNode, useCallback, useEffect, useState } from 'react';
import { FieldPath, FieldValues, PathValue } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { useRegisterSlideNavDirtyGetter } from '@/modules/presentation/clientPresentation/context/SlideNavGuard.context';

import { CustomSlide } from '../customSlide/CustomSlide';
import { SlideEditorCustomPage } from '../SlideEditor.types';
import { useIsTemplate } from '../textEditors/SlideEditor.promptContext';
import { useSlideFieldLiveDirty } from './useSlideFieldLiveDirty';

export interface FormAwareCustomSlideProps<TShape extends FieldValues> {
  page: SlideEditorCustomPage['page'];
  bundle: SlideEditorCustomPage['bundle'];
  /** Form path the body content commits to. */
  pageContentPath: FieldPath<TShape>;
  /** Optional form path the page name commits to. */
  pageNamePath?: FieldPath<TShape>;
  /**
   * Extra form paths the page name is mirrored to on every name commit (e.g.
   * a bundle's `displayName`). Ignored when `pageNamePath` is omitted.
   */
  additionalPageNamePaths?: FieldPath<TShape>[];
  /** Optional form path the AI prompt commits to. */
  customPromptPath?: FieldPath<TShape>;
  highlightVariables?: boolean;
  rightHeaderContent?: ReactNode;
  /**
   * Reports whether the slide has in-progress (pre-blur) edits. Lets consumers
   * (the template editor) confirm before discarding mid-keystroke edits that
   * haven't yet committed to the form.
   */
  onUnsavedEditsChange?: (hasUnsavedEdits: boolean) => void;
}

/**
 * Form-aware {@link CustomSlide}: binds the page-name/body/prompt fields of any
 * form (`TShape`) to the slide given their field paths. See
 * {@link FormAwareCoverSlide} for the shared commit + live-dirty behavior.
 */
export function FormAwareCustomSlide<TShape extends FieldValues>({
  page,
  bundle,
  pageContentPath,
  pageNamePath,
  additionalPageNamePaths,
  customPromptPath,
  highlightVariables,
  rightHeaderContent,
  onUnsavedEditsChange,
}: FormAwareCustomSlideProps<TShape>) {
  const { setValue } = useFormContext<TShape>();
  // Matches usePromptDisplayContent: the prompt is only the editable body (and
  // setting a prompt clears the content) in template mode. In live
  // presentations the editor always edits the content, prompt or not.
  const isTemplate = useIsTemplate();

  const commit = useCallback(
    (path: FieldPath<TShape>, value: string | null) => {
      // The caller types these paths to the slide's string text fields
      // (content/name/prompt), but TS can't prove the value type for a generic
      // TShape, so assert it. Not a path cast — the paths themselves are checked
      // at the call site.
      setValue(path, value as PathValue<TShape, typeof path>, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [setValue]
  );

  // In template mode an active prompt makes the body editor hold the prompt
  // text, so compare the live body against the prompt; otherwise (and in live
  // presentations) compare against the page content.
  const bodySeed =
    isTemplate && page.customPrompt ? page.customPrompt : page.pageContent;
  const {
    isDirty: nameDirty,
    onLiveChange: onNameLiveChange,
    reset: resetName,
  } = useSlideFieldLiveDirty(page.pageName);
  const {
    isDirty: bodyDirty,
    onLiveChange: onBodyLiveChange,
    reset: resetBody,
  } = useSlideFieldLiveDirty(bodySeed);
  const hasUnsavedEdits = nameDirty || bodyDirty;
  // Browser-level navigation (refresh, close, history).
  useOnBeforeUnloadPrompt(hasUnsavedEdits);
  // Surface live-dirty so consumers can confirm before discarding even
  // mid-keystroke, before the editor blurs and commits to the form.
  useEffect(() => {
    onUnsavedEditsChange?.(hasUnsavedEdits);
    return () => onUnsavedEditsChange?.(false);
  }, [hasUnsavedEdits, onUnsavedEditsChange]);
  // Remounts the slide on discard (see handleDiscard) so the editors re-seed
  // their DOM from the committed form values.
  const [revertKey, setRevertKey] = useState(0);
  const handleDiscard = useCallback(() => {
    // Clear the live-dirty tracking AND remount the editors: they own their DOM
    // content uncontrolled, so without a remount a later blur could commit the
    // abandoned text even after the user chose "Discard changes".
    resetName();
    resetBody();
    setRevertKey((k) => k + 1);
  }, [resetName, resetBody]);
  // In-app slide-to-slide navigation in the presentation designer. No-op in the
  // template editor, which has no SlideNavGuard provider.
  useRegisterSlideNavDirtyGetter(hasUnsavedEdits, handleDiscard);

  const onPageContentChange = useCallback(
    (value: string) => commit(pageContentPath, value),
    [commit, pageContentPath]
  );

  const onPageNameChange = useCallback(
    (value: string) => {
      if (!pageNamePath) return;
      commit(pageNamePath, value);
      additionalPageNamePaths?.forEach((path) => commit(path, value));
    },
    [commit, pageNamePath, additionalPageNamePaths]
  );

  const onPromptChange = useCallback(
    (newPrompt: string | null) => {
      if (!customPromptPath) return;
      commit(customPromptPath, newPrompt);
      // Template mode only: setting a prompt clears the body content (the
      // editor switches to editing the prompt). In live presentations the body
      // must be preserved — generation goes through an accept/reject step, so
      // clearing here could drop content if the user rejects or it fails.
      if (isTemplate && newPrompt) commit(pageContentPath, '');
    },
    [commit, customPromptPath, pageContentPath, isTemplate]
  );

  return (
    <CustomSlide
      key={revertKey}
      bundle={bundle}
      page={page}
      onPageContentChange={onPageContentChange}
      onContentGenerated={onPageContentChange}
      onPageNameChange={pageNamePath ? onPageNameChange : undefined}
      onPromptChange={customPromptPath ? onPromptChange : undefined}
      onPageContentInput={onBodyLiveChange}
      onPageNameInput={onNameLiveChange}
      highlightVariables={highlightVariables}
      rightHeaderContent={rightHeaderContent}
    />
  );
}
